/**
 * @description 同源多项目部署时的本地存储键前缀（Cookie / localStorage / sessionStorage）
 * @reason 与 IPC 等同域并存时，避免共用裸键名导致登录态与业务缓存串数据
 */

const DEFAULT_PREFIX = 'tdc'

/**
 * @description 解析存储前缀
 * @param {{ storagePrefix?: string }} [publicConfig]
 * @returns {string}
 */
export function resolveStoragePrefix(publicConfig) {
  const fromConfig = String(publicConfig?.storagePrefix ?? '').trim()
  if (fromConfig) return fromConfig
  const fromEnv = String(process.env.NUXT_PUBLIC_STORAGE_PREFIX ?? '').trim()
  return fromEnv || DEFAULT_PREFIX
}

/**
 * @description 为存储键添加项目前缀
 * @param {string} key
 * @param {string} [prefix]
 * @returns {string}
 */
export function storageKey(key, prefix) {
  const p = prefix ?? resolveStoragePrefixFromRuntime()
  if (!p) return key
  return `${p}-${key}`
}

/**
 * @returns {string}
 */
function resolveStoragePrefixFromRuntime() {
  try {
    const config = useRuntimeConfig()
    return resolveStoragePrefix(config.public)
  } catch {
    return resolveStoragePrefix()
  }
}

/** @description 常用裸键名（逻辑名，写入前须经 storageKey 加前缀） */
export const STORAGE_NAMES = {
  ACCESS_TOKEN: 'ims-access-token',
  REFRESH_TOKEN: 'ims-refresh-token',
  LOGIN: 'tenda-login',
  REDIRECT_BACK: 'tenda-redirect-back',
  USER_PERSIST: 'tenda-user',
  SEARCH_HISTORY: 'searchHistory',
  MOBILE_SEARCH_HISTORY: 'mobileSearchHistory',
  HELP_FEEDBACK: 'help-feedback',
  STORE_PAY_PENDING: 'store-pay-pending',
  STORE_PAY_TRANSFER_INFO: 'store-pay-transfer-info',
  ORDER_CONFIRM_BUY_NOW: 'tenda:store:orderConfirm:buyNowItems',
  TRACK_PRODUCT_ENTRY_SOURCE: 'track-product-entry-source',
  B2C_STEP: 'b2cStep',
  B2C_FLOW_LOG: 'b2cFlowAnalysisLogDto',
  B2C_SESSION_TS: 'b2cSessionTimeStamp',
  QIYU_GUEST_ID: 'qiyu-guest-id',
}

/**
 * @description 认证相关 Cookie / Pinia 持久化键名
 * @param {{ storagePrefix?: string }} [publicConfig]
 * @returns {{ accessTokenCookie: string, refreshTokenCookie: string, loginCookie: string, redirectBackCookie: string, userPersistKey: string }}
 */
export function getAuthStorageKeys(publicConfig) {
  const prefix = resolveStoragePrefix(publicConfig)
  return {
    accessTokenCookie: storageKey(STORAGE_NAMES.ACCESS_TOKEN, prefix),
    refreshTokenCookie: storageKey(STORAGE_NAMES.REFRESH_TOKEN, prefix),
    loginCookie: storageKey(STORAGE_NAMES.LOGIN, prefix),
    redirectBackCookie: storageKey(STORAGE_NAMES.REDIRECT_BACK, prefix),
    userPersistKey: storageKey(STORAGE_NAMES.USER_PERSIST, prefix),
  }
}

/**
 * @description Cookie 默认选项
 * @param {Object} [extra]
 * @returns {Object}
 */
export function getAuthCookieOptions(extra = {}) {
  return {
    path: '/',
    sameSite: 'lax',
    ...extra,
  }
}

/**
 * @description Pinia user store 命名空间 localStorage 适配器
 * @param {() => string} getPrefix
 * @returns {Storage}
 */
export function createPrefixedPersistStorage(getPrefix) {
  return {
    getItem() {
      if (typeof localStorage === 'undefined') return null
      return localStorage.getItem(storageKey(STORAGE_NAMES.USER_PERSIST, getPrefix()))
    },
    setItem(_key, value) {
      if (typeof localStorage === 'undefined') return
      localStorage.setItem(storageKey(STORAGE_NAMES.USER_PERSIST, getPrefix()), value)
    },
    removeItem() {
      if (typeof localStorage === 'undefined') return
      localStorage.removeItem(storageKey(STORAGE_NAMES.USER_PERSIST, getPrefix()))
    },
  }
}
